import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { useState, useRef, useEffect } from 'react';
import TagControl from './TagControl';
import pdfSettings from '../pdfSettings.json';
import { CustomFileUpload } from './CustomFileUpload';
import { Toast } from 'primereact/toast';
import { fetchAccessToken, apiCallErrorHandler } from '../utils/common';
import { useAuth0 } from '@auth0/auth0-react';
import { Dialog } from 'primereact/dialog';
import * as XLSX from 'xlsx';
import { downloadCompressed } from '../utils/pdfUtils';
import { ProgressBar } from 'primereact/progressbar';

export const PdfAnonymizerForm = () => {
    const { getAccessTokenSilently } = useAuth0();
    useEffect(() => { document.title = 'OptiConfig PDF Anonymizer'; }, []);

    const [customPresetName, setCustomPresetName] = useState('');
    const [allPresets, setAllPresets] = useState([]);

    const [token, setToken] = useState("");

    // Load presets from API when token is ready
    useEffect(() => {
        const loadPresets = async () => {
            if (!token) return;
            try {
                const resp = await fetch(`${process.env.REACT_APP_API_URL || ''}/pdf/presets`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                });
                if (!resp.ok) await apiCallErrorHandler(resp, 'Failed to load presets');
                const data = await resp.json();
                if (Array.isArray(data) && data.length > 0) {
                    setAllPresets(data);
                } else {
                    // fallback to bundled presets if server returns empty
                    setAllPresets([...pdfSettings.presets]);
                }
            } catch (err) {
                showError(err.message || 'Could not load presets');
                // fallback
                setAllPresets([...pdfSettings.presets]);
            }
        };
        loadPresets();
    }, [token]);

    const presetOptions = allPresets.map(p => ({ label: p.name, value: p.name }));

    const saveCustomPreset = async () => {
        if (!customPresetName.trim()) return;
        const newPreset = {
            name: customPresetName.trim(),
            replacements,
            cropTop,
            cropTopFirstPage: cropTopFirst,
            cropBottom,
            cropBottomFirstPage: cropBottomFirst
        };
        try {
            const resp = await fetch(`${process.env.REACT_APP_API_URL || ''}/pdf/presets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(newPreset)
            });
            if (!resp.ok) await apiCallErrorHandler(resp, 'Failed to save preset');
            // Optimistically update local list (avoid duplicate by name)
            setAllPresets(prev => {
                const idx = prev.findIndex(p => p.name === newPreset.name);
                if (idx !== -1) {
                    const copy = [...prev];
                    copy[idx] = newPreset;
                    return copy;
                }
                return [...prev, newPreset];
            });
            setPreset(newPreset.name);
            showSuccess('Preset saved!');
        } catch (err) {
            showError(err.message || 'Failed to save preset');
        }
    };

    const toast = useRef(null);

    const showError = (err) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: err,
                life: 3000
            });
        }
    };

    const showSuccess = (msg) => {
        if (toast.current) {
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: msg,
                life: 3000
            });
        }
    };

    const [preset, setPreset] = useState(null);
    const [cropTop, setCropTop] = useState(10);
    const [cropTopFirst, setCropTopFirst] = useState(null);
    const [cropBottom, setCropBottom] = useState(10);
    const [cropBottomFirst, setCropBottomFirst] = useState(null);
    const [replacements, setReplacements] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [sessionId, setSessionId] = useState(crypto.randomUUID());
    const [succeeded, setSucceeded] = useState(false);
    const [resultData, setResultData] = useState(null);
    const [showResult, setShowResult] = useState(false);

    // Progress dialog state
    const [progress, setProgress] = useState({ processed: 0, total: 0 });
    const [isProcessing, setIsProcessing] = useState(false);
    const [progressDialogVisible, setProgressDialogVisible] = useState(false);
    const [errorFiles, setErrorFiles] = useState([]);

    useEffect(() => {
        const found = allPresets.find(p => p.name === preset);
        if (found) {
            setReplacements(found.replacements || []);
            setCropTop(found.cropTop ?? 10);
            setCropTopFirst(found.cropTopFirstPage ?? null);
            setCropBottom(found.cropBottom ?? 10);
            setCropBottomFirst(found.cropBottomFirstPage ?? null);
        } else {
            setReplacements([]);
            setCropTop(0);
            setCropTopFirst(null);
            setCropBottom(0);
            setCropBottomFirst(null);
        }
    }, [preset, allPresets]);

    useEffect(() => {
        if (preset) {
            setCustomPresetName(preset);
        }
        // eslint-disable-next-line
    }, [preset]);

    useEffect(() => {
        const fetchToken = async () => {
            try {
                const accessToken = await fetchAccessToken(getAccessTokenSilently);
                setToken(accessToken);
            } catch (error) {
                console.error('Error fetching access token:', error);
                showError('Failed to fetch access token. Please try again.');
            }
        };
        fetchToken();
    }, [getAccessTokenSilently]);

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!uploadedFiles || uploadedFiles.length === 0) {
            showError('Please upload at least one PDF file.');
            return;
        }

        setSubmitting(true);

        const fileNames = uploadedFiles.map(file => file.name);

        // Chunking setup
        const chunkSize = 3; // adjust as needed
        const totalFiles = fileNames.length;

        // Aggregators
        const aggStats = {};
        const aggErrors = {};

        const mergeStats = (target, part) => {
            if (!part) return;
            Object.entries(part).forEach(([file, obj]) => {
                if (!target[file]) target[file] = {};
                Object.entries(obj || {}).forEach(([text, count]) => {
                    const n = Number(count) || 0;
                    target[file][text] = (Number(target[file][text]) || 0) + n;
                });
            });
        };

        const mergeErrors = (target, part) => {
            if (!part) return;
            Object.entries(part).forEach(([file, err]) => {
                target[file] = err;
            });
        };

        const postAnonymizeChunk = async (fileNamesChunk, chunkIndex) => {
            const payload = {
                fileNames: fileNamesChunk,
                cropTop: cropTop ?? '',
                cropTopFirst: cropTopFirst ?? '',
                cropBottom: cropBottom ?? '',
                cropBottomFirst: cropBottomFirst ?? '',
                replacements
            };

            const response = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/pdf/anonymize/${sessionId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) await apiCallErrorHandler(response, `Failed to process chunk ${chunkIndex + 1}`);

            const data = await response.json().catch(() => ({}));
            return data; // expected shape: { stats?: {...}, errors?: {...} }
        };

        try {
            // Show progress dialog
            setProgress({ processed: 0, total: totalFiles });
            setErrorFiles([]);
            setIsProcessing(true);
            setProgressDialogVisible(true);

            for (let i = 0; i < totalFiles; i += chunkSize) {
                const chunk = fileNames.slice(i, i + chunkSize);
                const chunkIdx = i / chunkSize;

                const res = await postAnonymizeChunk(chunk, chunkIdx);

                mergeStats(aggStats, res?.stats);
                mergeErrors(aggErrors, res?.errors);

                // Update UI state
                setErrorFiles(prev => {
                    const add = Object.keys(res?.errors || {});
                    return add.length ? [...prev, ...add] : prev;
                });
                setProgress(prev => ({
                    processed: Math.min(prev.processed + chunk.length, prev.total),
                    total: prev.total
                }));
            }

            const finalResult = { stats: aggStats, errors: aggErrors };
            setResultData(finalResult);
            setShowResult(true);

            if (Object.keys(aggStats).length === 0) {
                // Every file failed to process - there is nothing to download.
                setSucceeded(false);
                showError('All files failed to process. See errors for details.');
            } else {
                setSucceeded(true);
                showSuccess('Files processed successfully');
            }
        } catch (error) {
            showError(error.message || 'Submission failed.');
        } finally {
            setIsProcessing(false);
            setSubmitting(false);
        }
    };

    const discardSession = async (e) => {
        e?.preventDefault();
        try {
            await fetch(`${process.env.REACT_APP_API_URL || ''}/file/discard/${sessionId}` , {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            });
        } catch (err) {
            showError(err.message || 'Failed to discard session on server.');
        } finally {
            setUploadedFiles([]);
            setSucceeded(false);
            setSessionId(crypto.randomUUID());
            setResultData(null);
            setShowResult(false);
        }
    };

    const onDownload = async (e) => {
        e.preventDefault();

        setSubmitting(true);
        try {
            await downloadCompressed(sessionId, token);
            showSuccess('File downloaded successfully');
            await discardSession();
        } catch (error) {
            showError(error.message || 'Download failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const exportStatsToExcel = () => {
        if (!resultData || !resultData.stats) return;
        const files = Object.keys(resultData.stats);
        const allKeys = Array.from(new Set(
            Object.values(resultData.stats).flatMap(obj => Object.keys(obj))
        ));
        const header = ['Text', ...files];
        const rows = allKeys.map(key => [
            key,
            ...files.map(f => resultData.stats[f][key] !== undefined ? resultData.stats[f][key] : '')
        ]);
        const totals = files.map(file => Object.values(resultData.stats[file]).reduce((acc, val) => {
            const num = Number(val);
            return !isNaN(num) ? acc + num : acc;
        }, 0));
        rows.push(['Total', ...totals]);
        const aoa = [header, ...rows];
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        XLSX.utils.book_append_sheet(wb, ws, 'Stats');
        XLSX.writeFile(wb, `replacement_stats.xlsx`);
    };

    return (
        <form className="grid" style={{ maxWidth: '100vw', overflowX: 'auto' }} onSubmit={onSubmit}>
            <Toast ref={toast} />

            {/* Progress Dialog */}
            <Dialog
                header="Anonymization Progress"
                visible={progressDialogVisible}
                style={{ width: '75vw', maxWidth: '1200px' }}
                onHide={() => setProgressDialogVisible(false)}
                closable={!isProcessing}
                dismissableMask={!isProcessing}
                draggable={false}
                resizable={false}
                modal
            >
                <div className="p-fluid" style={{ fontSize: '0.8rem', lineHeight: 1.25 }}>
                    <div className="field">
                        <strong>Progress:</strong>
                        <div style={{ marginTop: 8 }}>
                            <ProgressBar
                                value={
                                    progress.total > 0
                                        ? Math.round((progress.processed / progress.total) * 100)
                                        : 0
                                }
                            />
                            <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
                                {isProcessing
                                    ? `Processing ${progress.processed} / ${progress.total} files...`
                                    : progress.total > 0
                                        ? `Processed ${progress.processed} / ${progress.total} files`
                                        : 'Idle'}
                            </div>
                        </div>
                    </div>
                    {errorFiles.length > 0 && (
                        <div className="field">
                            <strong>Errors:</strong>
                            <ul style={{ maxHeight: 200, overflowY: 'auto', paddingLeft: 16, fontSize: '0.75rem', color: '#b00020' }}>
                                {errorFiles.map((fname, idx) => (
                                    <li key={`err-${fname}-${idx}`}>{fname}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </Dialog>

            <div className="col-12">
                <div className="grid formgrid p-fluid">
                    <div className="field col-12">
                        <h4 style={{ textAlign: 'center' }}>PDF Anonymizer Tool</h4>
                    </div>
                    <div className='col-3'></div>
                    <div className="field col-6" style={{ display: 'flex', justifyContent: 'center' }}>
                        {!succeeded && <Button
                            type="submit"
                            label={submitting || isProcessing ? "Submitting..." : "Submit"}
                            className="p-button-primary"
                            style={{ width: '50%' }}
                            disabled={uploadedFiles.length === 0 || submitting || isProcessing}
                        />}
                        {succeeded && <>
                            <Button
                                onClick={onDownload}
                                label={submitting ? "Downloading..." : "Download .zip"}
                                className="p-button-primary"
                                style={{ width: '50%' }}
                                disabled={uploadedFiles.length === 0 || submitting || isProcessing}
                            />
                            <Button
                                type="button"
                                label={"Show Results"}
                                className="p-button-secondary"
                                onClick={() => setShowResult(true)}
                                style={{ width: '50%', marginLeft: 20 }}
                                disabled={!resultData || isProcessing}
                            />
                        </>}
                        {uploadedFiles.length > 0 && (
                            <Button
                                onClick={discardSession}
                                label={"Discard"}
                                className="p-button-secondary"
                                style={{ width: '50%', marginLeft: 20 }}
                                disabled={submitting || isProcessing}
                            />
                        )}
                    </div>
                    <div className='col-3'></div>
                    <div className="field col-6" style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                        <CustomFileUpload
                            acceptedFileTypes={['application/pdf', '.pdf']}
                            onMessage={(msg) => { showError(msg.detail); }}
                            token={token}
                            fileValue={uploadedFiles}
                            sessionId={sessionId}
                            onChangeFiles={(files) => {
                                setSucceeded(false);
                                setUploadedFiles(files);
                            }}
                        />
                    </div>
                    <div className="field col-6" style={{ paddingLeft: '0rem', paddingRight: '1rem' }}>
                        <div className="p-field">
                            <label htmlFor="presetDropdown">Presets</label>
                            <Dropdown
                                id="presetDropdown"
                                value={preset}
                                options={presetOptions}
                                onChange={(e) => setPreset(e.value)}
                                placeholder="Select a preset"
                                className="w-full"
                            />
                        </div>
                        <div className="grid" style={{ marginTop: '1rem' }}>
                            <div className="col-12" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
                                <label htmlFor="listOfReplacements">List of Replacements</label>
                                <TagControl
                                    placeholder="Specify text to remove (e.g., 'Manheim')"
                                    tags={replacements}
                                    onChange={(tags) => {
                                        setReplacements(tags);
                                    }} />
                            </div>
                            <div className="col-6">
                                <div className="p-field">
                                    <label htmlFor="cropTop">Crop From Top (%)</label>
                                    <InputNumber
                                        id="cropTop"
                                        value={cropTop}
                                        onValueChange={(e) => setCropTop(e.value)}
                                        min={0}
                                        max={99}
                                        suffix="%"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="p-field">
                                    <label htmlFor="cropTopFirst">Crop From Top (First Page) (%)</label>
                                    <InputNumber
                                        id="cropTopFirst"
                                        value={cropTopFirst}
                                        onValueChange={(e) => setCropTopFirst(e.value)}
                                        min={0}
                                        max={10000}
                                        suffix="%"
                                        className="w-full"
                                        placeholder="Leave empty for the same"
                                    />
                                </div>
                            </div>
                            <div className="col-6" style={{ marginTop: '1rem' }}>
                                <div className="p-field">
                                    <label htmlFor="cropBottom">Crop From Bottom (%)</label>
                                    <InputNumber
                                        id="cropBottom"
                                        value={cropBottom}
                                        onValueChange={(e) => setCropBottom(e.value)}
                                        min={0}
                                        max={99}
                                        suffix="%"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            <div className="col-6" style={{ marginTop: '1rem' }}>
                                <div className="p-field">
                                    <label htmlFor="cropBottomFirst">Crop From Bottom (First Page) (%)</label>
                                    <InputNumber
                                        id="cropBottomFirst"
                                        value={cropBottomFirst}
                                        onValueChange={(e) => setCropBottomFirst(e.value)}
                                        min={0}
                                        max={10000}
                                        suffix="%"
                                        className="w-full"
                                        placeholder="Leave empty for the same"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="field col-12" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, marginTop: '3rem' }}>
                            <div className="col-2"></div>
                            <div className="col-4">
                                <InputText
                                    type="text"
                                    value={customPresetName}
                                    onChange={e => setCustomPresetName(e.target.value)}
                                    placeholder="Preset name"
                                />
                            </div>
                            <div className="col-4">
                                <Button
                                    type="button"
                                    label="Save as Preset"
                                    className="p-button-secondary"
                                    onClick={saveCustomPreset}
                                    disabled={!customPresetName.trim()}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="field col-12" style={{ marginTop: 24 }}>
                        {resultData && (
                            <Dialog
                                header="Replacement Stats"
                                visible={showResult}
                                style={{ width: '80vw', maxWidth: '95vw' }}
                                onHide={() => setShowResult(false)}
                                draggable={false}
                                resizable={false}
                                modal
                                footer={<div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                                    <Button label="Export to Excel" className="p-button-success" onClick={exportStatsToExcel} disabled={!resultData || !resultData.stats} />
                                    <Button label="Close" onClick={() => setShowResult(false)} className="p-button-secondary" />
                                </div>}
                            >
                                {resultData && resultData.stats && (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className="p-datatable-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8em' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ border: '1px solid #ccc', padding: 4 }}>Text</th>
                                                    {Object.keys(resultData.stats).map((file, idx) => (
                                                        <th key={file + idx} style={{ border: '1px solid #ccc', padding: 4 }}>{file}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const allKeys = Array.from(new Set(
                                                        Object.values(resultData.stats).flatMap(obj => Object.keys(obj))
                                                    ));
                                                    return allKeys.map((key) => (
                                                        <tr key={key}>
                                                            <td style={{ border: '1px solid #ccc', padding: 4 }}>{key}</td>
                                                            {Object.keys(resultData.stats).map((file, idx) => (
                                                                <td key={file + key + idx} style={{ border: '1px solid #ccc', padding: 4 }}>
                                                                    {resultData.stats[file][key] !== undefined ? resultData.stats[file][key] : ''}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ));
                                                })()}
                                                {/* Total row */}
                                                <tr style={{ fontWeight: 'bold', background: '#f5f5f5' }}>
                                                    <td style={{ border: '1px solid #ccc', padding: 4 }}>Total</td>
                                                    {Object.keys(resultData.stats).map((file, idx) => {
                                                        // Sum all numeric values for this file
                                                        const sum = Object.values(resultData.stats[file]).reduce((acc, val) => {
                                                            const num = Number(val);
                                                            return !isNaN(num) ? acc + num : acc;
                                                        }, 0);
                                                        return (
                                                            <td key={file + 'total' + idx} style={{ border: '1px solid #ccc', padding: 4 }}>{sum}</td>
                                                        );
                                                    })}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {resultData && resultData.errors && Object.keys(resultData.errors).length > 0 && (
                                    <div style={{ marginTop: 16 }}>
                                        <h5>Errors</h5>
                                        <table className="p-datatable-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ border: '1px solid #ccc', padding: 4 }}>File</th>
                                                    <th style={{ border: '1px solid #ccc', padding: 4 }}>Error</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(resultData.errors).map(([file, err]) => (
                                                    <tr key={file}>
                                                        <td style={{ border: '1px solid #ccc', padding: 4 }}>{file}</td>
                                                        <td style={{ border: '1px solid #ccc', padding: 4 }}>{err}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Dialog>
                        )}
                    </div>
                </div>
            </div>
        </form>
    );
};